/**
 * Large super-admin tenant CRUD UI; kept as one module until split into smaller components.
 */
/* eslint-disable max-lines-per-function, complexity */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vehix/portal/components/ui/card';
import { Button } from '@vehix/portal/components/ui/button';
import { Badge } from '@vehix/portal/components/ui/badge';
import { Input } from '@vehix/portal/components/ui/input';
import { Label } from '@vehix/portal/components/ui/label';
import { Textarea } from '@vehix/portal/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@vehix/portal/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@vehix/portal/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@vehix/portal/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@vehix/portal/components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@vehix/portal/components/ui/tabs';
import { Checkbox } from '@vehix/portal/components/ui/checkbox';
import { useToast } from '@vehix/shared/admin/portal-bridge';
import {
  AlertTriangle,
  BadgeCheck,
  Building2,
  Plus,
  Edit,
  Trash2,
  RotateCcw,
  Search,
  MapPin,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  Info,
  ChevronDown,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { tenantAPI, Tenant, CreateTenantData, UpdateTenantData } from '@vehix/portal/services/api/tenants';
import { useAllTenants } from '@vehix/portal/hooks/useTenants';
import { useQueryClient } from '@tanstack/react-query';
import { StatCard } from './dashboard/StatCard';
import { getOemTheme } from '../utils/oemColors';
import { ZoneAssignmentPanel, type ZoneOwner } from './ZoneAssignmentPanel';
import { getPayingCustomerIds } from '../utils/payingCustomers';
import { cn } from '@vehix/portal/lib/utils';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider
} from '@vehix/portal/components/ui/tooltip';
import { KNOWN_OEMS } from '@vehix/portal/constants/oems';
import {
  useZonesPaginated,
  useCurrentRecallCounts,
  useZoneAssignmentDates
} from '../hooks/useZones';

const DEFAULT_TENANTS_PAGE_SIZE = 100;
const TENANTS_PAGE_SIZE_OPTIONS = [25, 50, 100, 200] as const;

const TENANTS_PAGINATION_FOOTER_CLASS =
  'flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between';

type TenantTypeTab = 'all' | 'internal' | 'dealer';

/**
 * Cap for the OEM picker's scroll area.
 *
 * Uses the popover's own available height (Radix sets this var from its collision
 * detection) so the list can never render taller than the space below the trigger —
 * which is what pushed its scrollbar off-screen and made the roster look truncated.
 */
const OEM_LIST_MAX_HEIGHT =
  'min(260px, calc(var(--radix-popover-content-available-height, 260px) - 3.25rem))';

/**
 * Drop the zone ids belonging to an OEM the admin has just deselected.
 *
 * Deliberately a blacklist rather than a whitelist. The previous version kept
 * only ids it could positively match against the loaded zone list, so any time
 * that list was empty — still loading, an RPC error, or more zones than the
 * query's page size — it returned [] and silently discarded every zone on the
 * tenant, with the loss surfacing only in the confirm diff at submit.
 *
 * Removing just the ids we can *prove* belong to the removed OEM fails safe: an
 * id we cannot resolve is kept, and the admin still reviews it in the diff
 * before anything is written.
 */
function zoneIdsWithoutOem(
  zoneIds: string[],
  zones: { id: string; oem: string }[],
  removedOem: string
): string[] {
  return zoneIds.filter(zoneId => {
    const zone = zones.find(z => z.id === zoneId);
    return !zone || zone.oem !== removedOem;
  });
}

export const TenantSetupTab: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  /**
   * Every tenant write here also changes zone ownership, so both caches go.
   *
   * Previously only ['tenants'] was invalidated, which left the Zone tab showing
   * a zone's previous owner for up to useZones' 10-minute staleTime — reassign a
   * zone, switch tabs, and the old tenant was still named. ['zones'] is the root
   * of zoneKeys, so this also clears the 'tenantMappings' entries that
   * useZones.ts never invalidates anywhere, not even in deleteZone (which
   * cascade-deletes prod.tenant_zones rows server-side).
   */
  const invalidateTenantAndZoneCaches = () => {
    queryClient.invalidateQueries({ queryKey: ['tenants'] });
    queryClient.invalidateQueries({ queryKey: ['zones'] });
  };

  const {
    data: tenants,
    isLoading: tenantsLoading,
    error: tenantsError,
    refetch
  } = useAllTenants();


  const [searchTerm, setSearchTerm] = useState('');
  const [tenantTypeTab, setTenantTypeTab] = useState<TenantTypeTab>('dealer');
  // Status filter — defaults to active-only because inactive tenants are
  // typically demo/prospect entries that clutter the working view. A
  // "Show inactive" toggle in the toolbar opens the door to inactive rows.
  const [showInactive, setShowInactive] = useState(false);
  const statusFilter: 'all' | 'active' = showInactive ? 'all' : 'active';
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  /**
   * OEM picker search.
   *
   * KNOWN_OEMS is 25 entries but the popover's scroll area only shows ~12, so
   * everything from Porsche down (Mazda, Subaru, Tesla, Rivian…) sat below the
   * fold. Because the popover overhangs the dialog edge its scrollbar is easy to
   * miss, which made the list read as "Mazda isn't supported" rather than
   * "scroll for more". A filter makes the whole roster reachable in one keystroke.
   */
  const [oemSearch, setOemSearch] = useState('');
  const visibleOems = KNOWN_OEMS.filter(oem =>
    oem.toLowerCase().includes(oemSearch.trim().toLowerCase())
  );

  /**
   * Which tenant the dialog is currently editing, for discarding late responses.
   * openEditDialog revalidates zones in the background; if the admin has since
   * closed the dialog or opened a different tenant, that response must not be
   * applied to whatever form is on screen now.
   */
  const openTenantIdRef = React.useRef<string | null>(null);

  // Clear on close. Keyed off `dialogOpen` rather than the close handlers
  // because the dialog is dismissed four different ways — Radix onOpenChange,
  // the Cancel button, a successful save, and Escape — and every one of them
  // must invalidate an in-flight zone refresh.
  useEffect(() => {
    if (!dialogOpen) openTenantIdRef.current = null;
  }, [dialogOpen]);
  const [tenantsPageIndex, setTenantsPageIndex] = useState(0);
  const [tenantsPageSize, setTenantsPageSize] = useState(DEFAULT_TENANTS_PAGE_SIZE);

  // Form state
  const [formData, setFormData] = useState<{
    tenant_name: string;
    tenant_slug: string;
    tenant_type: string;
    status: string;
    time_zone: string;
    oems: string[]; // Multi-OEM support: array of OEM names
    description: string;
    primary_email: string;
    primary_phone: string;
    website: string;
    address_line1: string;
    address_line2: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
    latitude: string;
    longitude: string;
    zone_ids: string[];
  }>({
    tenant_name: '',
    tenant_slug: '',
    tenant_type: '',
    status: 'active',
    time_zone: '',
    oems: [] as string[], // Multi-OEM: start with empty array
    description: '',
    primary_email: '',
    primary_phone: '',
    website: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    latitude: '',
    longitude: '',
    zone_ids: [] as string[]
  });

  // One zone query for every tenant, not one per OEM combination.
  //
  // useZonesPaginated's cache key includes the OEM filter, so passing this
  // tenant's OEMs gave every distinct OEM set its own cache entry: opening a
  // Ford tenant and then a Kia one refetched from scratch each time, and
  // refetchOnMount:false cannot help a key that was never populated. Fetching
  // every active zone under one stable key makes the dialog instant from the
  // second open onwards; filtering ~190 rows in memory costs nothing.
  const { data: zonesData, isLoading: loadingZones } = useZonesPaginated(
    1, // page
    500, // limit - comfortably covers all active zones (190 today)
    undefined, // searchTerm
    'active', // statusFilter - only active zones
    undefined, // regionFilter
    undefined, // oemFilter - deliberately unfiltered so the key stays stable
    false // includeUserNames - not needed for selection
  );

  /** Every active zone, unfiltered — the resolution source for zone names. */
  const allActiveZones = React.useMemo(() => zonesData?.zones ?? [], [zonesData]);

  /**
   * The zones this tenant may choose from. Internal tenants implicitly get all
   * zones; dealers see only zones belonging to their selected OEMs.
   */
  const filteredZones = React.useMemo(() => {
    if (formData.tenant_type === 'internal') return allActiveZones;
    if (formData.oems.length === 0) return [];
    return allActiveZones.filter(z => formData.oems.includes(z.oem));
  }, [allActiveZones, formData.tenant_type, formData.oems]);

  /**
   * zone_id -> every tenant that currently owns it.
   *
   * Built by inverting the tenant list rather than calling useZoneTenantMappings,
   * which returns Record<zone_id, tenant_name> — one name per zone, last row
   * wins — and therefore cannot represent a zone held by two tenants at all.
   * That is why the Zone tab's "Assigned" KPI read as healthy while six zones
   * were double-allocated.
   *
   * getAllTenants already hydrates `zones` on every tenant, so this is a free,
   * lossless inversion of data that is already in memory: no extra query, and
   * correct for N owners.
   */
  /** Drives the "Assigned since" column on blocked zones. */
  const { data: zoneAssignmentDates } = useZoneAssignmentDates();

  const zoneOwners = React.useMemo(() => {
    const map = new Map<string, ZoneOwner[]>();
    for (const tenant of tenants ?? []) {
      for (const zone of tenant.zones ?? []) {
        const owner: ZoneOwner = { id: tenant.id, tenant_name: tenant.tenant_name };
        const existing = map.get(zone.id);
        if (existing) existing.push(owner);
        else map.set(zone.id, [owner]);
      }
    }
    return map;
  }, [tenants]);

  // Defensive confirmation state — zone-change diff (edit) and delete.
  // Both flows intentionally interrupt the user before any destructive
  // server call so accidental clicks can't reshape a customer's experience.
  type ZoneDiffEntry = { id: string; zone_name: string; oem?: string | undefined };
  const [zoneDiffConfirmOpen, setZoneDiffConfirmOpen] = useState(false);
  const [zoneDiff, setZoneDiff] = useState<{
    added: ZoneDiffEntry[];
    removed: ZoneDiffEntry[];
  }>({ added: [], removed: [] });

  /**
   * Recall volume for the zones about to be removed.
   *
   * Unassigning a zone cuts this tenant's access to its recalls, so the confirm
   * dialog should say how much work is being taken away rather than only naming
   * the zone. useCurrentRecallCounts canonicalizes (sorts + dedupes) its id list,
   * so this shares a cache entry with any other caller asking about the same set.
   */
  const removedZoneIds = React.useMemo(() => zoneDiff.removed.map(z => z.id), [zoneDiff.removed]);
  const { data: removedRecallData } = useCurrentRecallCounts(removedZoneIds, editingTenant?.id);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState<Tenant | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Per-row restore in flight — disables the row's restore button while
  // the API call is pending so a double-click can't fire two requests.
  // We track by tenant id rather than a single boolean so other rows in
  // the inactive view stay clickable independently.
  const [restoringTenantId, setRestoringTenantId] = useState<string | null>(null);

  const openCreateDialog = () => {
    setEditingTenant(null);
    setFormData({
      tenant_name: '',
      tenant_slug: '',
      tenant_type: '',
      status: 'active',
      time_zone: '',
      oems: [] as string[], // Multi-OEM: start with empty array
      description: '',
      primary_email: '',
      primary_phone: '',
      website: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'US',
      latitude: '',
      longitude: '',
      zone_ids: [] as string[]
    });
    setDialogOpen(true);
  };

  /**
   * Open the edit dialog immediately, then revalidate zones in the background.
   *
   * This used to `await tenantAPI.getTenantZones()` before setDialogOpen(true),
   * so every click on Edit paid a network round trip before anything appeared.
   * The await was also redundant: getAllTenants already hydrates `zone_ids` on
   * every row from that same call (tenants.ts:359-385). We seed from the list,
   * paint at once — as .claude/rules/instant-feedback.md requires — and adopt
   * fresher server data only if the admin has not already edited the selection.
   */
  const openEditDialog = (tenant: Tenant) => {
    const seededZoneIds: string[] = tenant.zone_ids || [];
    openTenantIdRef.current = tenant.id;
    setEditingTenant(tenant);

    setFormData({
      tenant_name: tenant.tenant_name || '',
      tenant_slug: tenant.tenant_slug || '',
      tenant_type: tenant.tenant_type || '',
      status: tenant.status || 'active',
      time_zone: tenant.time_zone || '',
      // Multi-OEM: prefer oems array, fallback to single oem for backward compat
      oems: tenant.oems && tenant.oems.length > 0 ? tenant.oems : tenant.oem ? [tenant.oem] : [],
      description: tenant.description || '',
      primary_email: tenant.primary_email || '',
      primary_phone: tenant.primary_phone || '',
      website: tenant.website || '',
      address_line1: tenant.address_line1 || '',
      address_line2: tenant.address_line2 || '',
      city: tenant.city || '',
      state: tenant.state || '',
      postal_code: tenant.postal_code || '',
      country: tenant.country || 'US',
      latitude: tenant.latitude?.toString() || '',
      longitude: tenant.longitude?.toString() || '',
      zone_ids: seededZoneIds
    });
    setDialogOpen(true);

    // Dialog is already on screen; refresh zones behind it.
    void tenantAPI
      .getTenantZones(tenant.id)
      .then(zones => {
        // Dialog closed, or moved to another tenant, while this was in flight.
        if (openTenantIdRef.current !== tenant.id) return;

        const fresh = zones.map(z => z.id);
        setFormData(prev => {
          const untouched =
            prev.zone_ids.length === seededZoneIds.length &&
            prev.zone_ids.every(id => seededZoneIds.includes(id));
          // Never overwrite a selection the admin has already changed.
          return untouched ? { ...prev, zone_ids: fresh } : prev;
        });
      })
      .catch(() => {
        // Keep the seeded ids; the list's copy came from this same source.
      });
  };

  /**
   * Resolve a zone ID to display info (name + OEM).
   *
   * Consults `allActiveZones` first: it is unfiltered, so unlike the old
   * `filteredZones` it still resolves zones whose OEM has just been deselected,
   * and it always carries `oem`. The `editingTenant.zones` snapshot is the
   * fallback — it covers zones that have since been deactivated and therefore
   * dropped out of the active list.
   */
  const resolveZoneInfo = (zoneId: string): ZoneDiffEntry => {
    const fromList = allActiveZones.find(z => z.id === zoneId);
    if (fromList) {
      return {
        id: zoneId,
        zone_name: fromList.zone_name,
        oem: fromList.oem
      };
    }
    const fromTenant = editingTenant?.zones?.find(z => z.id === zoneId);
    if (fromTenant) {
      return {
        id: zoneId,
        zone_name: fromTenant.zone_name,
        oem: fromTenant.oem
      };
    }
    // Fallback — zone exists but neither source has it (rare). Show ID.
    return { id: zoneId, zone_name: zoneId, oem: undefined };
  };

  /**
   * Compute the zone diff between an edit's "before" (editingTenant) and
   * "after" (formData) state. Returns null when nothing to confirm
   * (creating, or zones unchanged).
   */
  const computeZoneDiff = (): {
    added: ZoneDiffEntry[];
    removed: ZoneDiffEntry[];
  } | null => {
    if (!editingTenant) return null;
    const beforeIds = new Set(
      editingTenant.zones?.map(z => z.id) ?? editingTenant.zone_ids ?? []
    );
    const afterIds = new Set(formData.zone_ids);

    const added: ZoneDiffEntry[] = [];
    const removed: ZoneDiffEntry[] = [];

    afterIds.forEach(id => {
      if (!beforeIds.has(id)) added.push(resolveZoneInfo(id));
    });
    beforeIds.forEach(id => {
      if (!afterIds.has(id)) removed.push(resolveZoneInfo(id));
    });

    if (added.length === 0 && removed.length === 0) return null;
    return { added, removed };
  };

  const handleSubmit = async () => {
    if (!formData.tenant_name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Tenant name is required',
        variant: 'destructive'
      });
      return;
    }

    // Validate at least one OEM is required for dealer-type tenants
    if (formData.tenant_type === 'dealer') {
      if (formData.oems.length === 0) {
        toast({
          title: 'Validation Error',
          description: 'At least one OEM is required for dealer-type tenants',
          variant: 'destructive'
        });
        return;
      }
    }

    // An active dealer with no zones can see no recall data at all, so require
    // at least one. Scoped deliberately:
    //   - inactive tenants of any type are exempt (5 sit at zero zones today)
    //   - internal tenants are exempt; they receive every zone implicitly, and
    //     the 2 active ones legitimately have no tenant_zones rows
    if (
      formData.tenant_type === 'dealer' &&
      formData.status === 'active' &&
      formData.zone_ids.length === 0
    ) {
      toast({
        title: 'Validation Error',
        description:
          'Active dealers need at least one zone, otherwise they see no recall data. ' +
          'Assign one on the Zones tab, or set this tenant to inactive.',
        variant: 'destructive'
      });
      return;
    }

    // Validate coordinates are required for dealer-type tenants
    if (formData.tenant_type === 'dealer') {
      if (
        !formData.latitude ||
        !formData.longitude ||
        formData.latitude.trim() === '' ||
        formData.longitude.trim() === ''
      ) {
        toast({
          title: 'Validation Error',
          description: 'Latitude and longitude are required for dealer-type tenants',
          variant: 'destructive'
        });
        return;
      }

      // Validate coordinate ranges
      const lat = parseFloat(formData.latitude);
      const lng = parseFloat(formData.longitude);

      if (isNaN(lat) || lat < -90 || lat > 90) {
        toast({
          title: 'Validation Error',
          description: 'Latitude must be between -90 and 90',
          variant: 'destructive'
        });
        return;
      }

      if (isNaN(lng) || lng < -180 || lng > 180) {
        toast({
          title: 'Validation Error',
          description: 'Longitude must be between -180 and 180',
          variant: 'destructive'
        });
        return;
      }
    }

    // For edits, intercept before the server call if zones have changed.
    // Zone associations drive what data each customer can access, so a
    // change deserves an explicit confirmation step.
    if (editingTenant) {
      const diff = computeZoneDiff();
      if (diff) {
        setZoneDiff(diff);
        setZoneDiffConfirmOpen(true);
        return;
      }
    }

    await executeSubmit();
  };

  /**
   * The actual save path. Split out from handleSubmit so the zone-diff
   * confirm dialog can call into it after the user clicks "Confirm change".
   */
  const executeSubmit = async () => {
    try {
      setFormLoading(true);

      if (editingTenant) {
        // Update existing tenant
        const updateData: UpdateTenantData = {
          tenant_name: formData.tenant_name.trim(),
          tenant_slug: formData.tenant_slug.trim() || undefined,
          tenant_type: formData.tenant_type || undefined,
          status: formData.status,
          time_zone: formData.time_zone || undefined,
          oems: formData.oems.length > 0 ? formData.oems : undefined, // Multi-OEM array
          description: formData.description || undefined,
          primary_email: formData.primary_email || undefined,
          primary_phone: formData.primary_phone || undefined,
          website: formData.website || undefined,
          address_line1: formData.address_line1 || undefined,
          address_line2: formData.address_line2 || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          postal_code: formData.postal_code || undefined,
          country: formData.country || undefined,
          latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
          longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
          zone_ids: formData.zone_ids
        };

        await tenantAPI.updateTenant(editingTenant.id, updateData);

        toast({
          title: 'Tenant Updated',
          description: `Tenant "${formData.tenant_name}" has been updated successfully`
        });
      } else {
        // Create new tenant
        const createData: CreateTenantData = {
          tenant_name: formData.tenant_name.trim(),
          tenant_slug: formData.tenant_slug.trim() || undefined,
          tenant_type: formData.tenant_type || undefined,
          status: formData.status,
          time_zone: formData.time_zone || undefined,
          oems: formData.oems.length > 0 ? formData.oems : undefined, // Multi-OEM array
          description: formData.description || undefined,
          primary_email: formData.primary_email || undefined,
          primary_phone: formData.primary_phone || undefined,
          website: formData.website || undefined,
          address_line1: formData.address_line1 || undefined,
          address_line2: formData.address_line2 || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          postal_code: formData.postal_code || undefined,
          country: formData.country || undefined,
          latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
          longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
          zone_ids: formData.zone_ids
        };

        await tenantAPI.createTenant(createData);

        toast({
          title: 'Tenant Created',
          description: `Tenant "${formData.tenant_name}" has been created successfully`
        });
      }

      // Invalidate queries to refresh data
      invalidateTenantAndZoneCaches();

      setZoneDiffConfirmOpen(false);
      setDialogOpen(false);
      await refetch();
    } catch (error: unknown) {
      toast({
        title: 'Error',
        description:
          error instanceof Error
            ? error.message
            : `Failed to ${editingTenant ? 'update' : 'create'} tenant`,
        variant: 'destructive'
      });
    } finally {
      setFormLoading(false);
    }
  };

  /**
   * First step of the delete flow — opens the styled confirmation Dialog
   * instead of calling the API. The actual deletion happens in
   * `executeDelete` once the user confirms.
   */
  const handleDelete = (tenant: Tenant) => {
    setTenantToDelete(tenant);
    setDeleteConfirmOpen(true);
  };

  const executeDelete = async () => {
    if (!tenantToDelete) return;
    const tenant = tenantToDelete;

    try {
      setDeleteLoading(true);
      await tenantAPI.deleteTenant(tenant.id, false); // Soft delete

      toast({
        title: 'Tenant Deleted',
        description: `Tenant "${tenant.tenant_name}" has been deleted`
      });

      invalidateTenantAndZoneCaches();
      setDeleteConfirmOpen(false);
      setTenantToDelete(null);
      await refetch();
    } catch (error: unknown) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete tenant',
        variant: 'destructive'
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  /**
   * Restore a soft-deleted (inactive) tenant by flipping status back to
   * 'active'. Soft delete in this codebase is implemented as
   * `status='inactive'`, so restore is the symmetrical inverse —
   * `tenantAPI.updateTenant({ status: 'active' })`. No confirmation
   * dialog because restore is non-destructive: the action is
   * intentionally clicked, no data is altered beyond the status flag,
   * and the user can deactivate again instantly if needed.
   */
  const handleRestore = async (tenant: Tenant) => {
    try {
      setRestoringTenantId(tenant.id);
      await tenantAPI.updateTenant(tenant.id, { status: 'active' });

      toast({
        title: 'Tenant Restored',
        description: `Tenant "${tenant.tenant_name}" is active again`
      });

      invalidateTenantAndZoneCaches();
      await refetch();
    } catch (error: unknown) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to restore tenant',
        variant: 'destructive'
      });
    } finally {
      setRestoringTenantId(null);
    }
  };

  // Distinct tenant_type values from data (dialog shows extras beyond fixed SelectItem list)
  const tenantTypes = React.useMemo(() => {
    if (!tenants) return [];
    const types = new Set<string>();
    tenants.forEach(t => {
      if (t.tenant_type) {
        types.add(t.tenant_type);
      }
    });
    return Array.from(types).sort();
  }, [tenants]);

  // Filter tenants
  const filteredTenants = React.useMemo(() => {
    if (!tenants) return [];

    return tenants.filter(tenant => {
      // Search filter
      const matchesSearch =
        !searchTerm ||
        tenant.tenant_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.tenant_slug?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.tenant_type?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesTenantTypeTab = tenantTypeTab === 'all' || tenant.tenant_type === tenantTypeTab;

      // Status filter — 'all' shows everything; 'active' hides inactive
      const matchesStatus = statusFilter === 'all' || tenant.status === 'active';

      return matchesSearch && matchesTenantTypeTab && matchesStatus;
    });
  }, [tenants, searchTerm, tenantTypeTab, statusFilter]);

  // KPI metrics — derived from tenants. Active Tenants count is sourced
  // from payingCustomerIds (computed below) so the KPI always matches the
  // number of highlighted rows in the table — single source of truth.
  const kpiMetrics = React.useMemo(() => {
    const tenantList = tenants ?? [];
    const dealerTenants = tenantList.filter(t => t.tenant_type === 'dealer');
    const totalDealerTenants = dealerTenants.length;

    // Total zones across dealer tenants (sum of zones[] array length per dealer)
    const totalZonesInDealers = dealerTenants.reduce(
      (sum, t) => sum + (t.zones?.length ?? 0),
      0
    );

    return {
      totalDealerTenants,
      totalZonesInDealers
    };
  }, [tenants]);

  // Paying-customer set — see ../utils/payingCustomers for the heuristic.
  // Shared with User Setup so the "Avg Users / Tenant" denominator stays
  // in sync with the rows highlighted on this page.
  const payingCustomerIds = React.useMemo(() => getPayingCustomerIds(tenants), [tenants]);

  const filteredTenantCount = filteredTenants.length;
  const tenantsTotalPages = Math.max(1, Math.ceil(filteredTenantCount / tenantsPageSize));
  const tenantsSafePageIndex = Math.min(tenantsPageIndex, tenantsTotalPages - 1);
  const tenantsPageStart = tenantsSafePageIndex * tenantsPageSize;
  const paginatedTenants = filteredTenants.slice(
    tenantsPageStart,
    tenantsPageStart + tenantsPageSize
  );
  const tenantsRangeEnd =
    filteredTenantCount === 0
      ? 0
      : Math.min(tenantsPageStart + tenantsPageSize, filteredTenantCount);

  useEffect(() => {
    setTenantsPageIndex(0);
  }, [searchTerm, tenantTypeTab, statusFilter, tenantsPageSize]);

  useEffect(() => {
    setTenantsPageIndex(p => Math.min(p, Math.max(0, tenantsTotalPages - 1)));
  }, [tenantsTotalPages]);

  if (tenantsLoading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
      </div>
    );
  }

  if (tenantsError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2 text-destructive'>
            <AlertCircle className='h-5 w-5' />
            Error Loading Tenants
          </CardTitle>
          <CardDescription>
            {tenantsError instanceof Error ? tenantsError.message : 'Failed to load tenants'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => refetch()} variant='outline'>
            <RefreshCw className='h-4 w-4 mr-2' />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className='flex h-full w-full flex-col overflow-hidden'>
      {/* KPI strip — at-a-glance metrics for the page */}
      <div className='grid grid-cols-2 lg:grid-cols-3 gap-3 px-8 py-4 border-b flex-shrink-0 bg-muted/30'>
        <StatCard
          title='Dealer Tenants'
          value={kpiMetrics.totalDealerTenants}
          icon={Building2}
          loading={tenantsLoading}
        />
        <StatCard
          title='Active Tenants'
          value={payingCustomerIds.size}
          icon={CheckCircle}
          loading={tenantsLoading}
          description='Real paying customers'
        />
        <StatCard
          title='Zones in Dealers'
          value={kpiMetrics.totalZonesInDealers}
          icon={MapPin}
          loading={tenantsLoading}
        />
      </div>

      {/* Compact toolbar — filters + actions in one row, generous edge padding */}
      <div className='flex flex-wrap items-end gap-4 px-8 py-4 border-b flex-shrink-0'>
        <div className='space-y-1 flex-1 min-w-[220px] max-w-md'>
          <label className='text-xs font-medium text-muted-foreground'>Search</label>
          <div className='relative'>
            <Search className='absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
            <Input
              placeholder='Search tenants...'
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className='pl-8 h-9'
            />
          </div>
        </div>

        {/* Active is the default working set (paying customers). Toggle reveals
            inactive demo/prospect tenants when needed. */}
        <label className='flex items-center gap-2 h-9 px-3 rounded-md border bg-background hover:bg-accent cursor-pointer self-end'>
          <Checkbox
            checked={showInactive}
            onCheckedChange={checked => setShowInactive(checked === true)}
            aria-label='Show inactive tenants'
          />
          <span className='text-sm text-foreground select-none'>Show inactive</span>
        </label>

        <div className='space-y-1'>
          <label className='text-xs font-medium text-muted-foreground'>Type</label>
          <Tabs
            value={tenantTypeTab}
            onValueChange={v => setTenantTypeTab(v as TenantTypeTab)}
          >
            <TabsList className='h-9 p-1'>
              <TabsTrigger value='internal' className='h-7 px-3 text-xs'>
                Internal
              </TabsTrigger>
              <TabsTrigger value='dealer' className='h-7 px-3 text-xs'>
                Dealer
              </TabsTrigger>
              <TabsTrigger value='all' className='h-7 px-3 text-xs'>
                All
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className='flex-1' />

        {/* Result count + actions — bottom-aligned with input fields via self-end */}
        <span className='text-xs text-muted-foreground self-end pb-2.5'>
          {filteredTenants.length.toLocaleString()} tenant{filteredTenants.length === 1 ? '' : 's'}
        </span>

        <Button
          variant='ghost'
          size='sm'
          className='h-9 w-9 p-0 self-end'
          onClick={() => refetch()}
          title='Refresh'
          aria-label='Refresh tenants'
        >
          <RefreshCw className='h-4 w-4' />
        </Button>

        <Button onClick={openCreateDialog} className='h-9 self-end'>
          <Plus className='h-4 w-4 mr-1' />
          Add Tenant
        </Button>
      </div>

      {/* Table area — fills remaining vertical space, scrolls internally */}
      <div className='flex-1 min-h-0 overflow-y-auto px-8 py-6'>
        {filteredTenants.length === 0 ? (
            <div className='text-center py-12 text-muted-foreground'>
              <Building2 className='h-12 w-12 mx-auto mb-4 opacity-50' />
              <p>No tenants found</p>
              {tenants && tenants.length > 0 && (
                <p className='text-sm mt-2'>Try adjusting your filters</p>
              )}
            </div>
          ) : (
            <TooltipProvider delayDuration={150}>
            <div className='overflow-x-auto'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className='w-12 text-center'>#</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>OEM(s)</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className='text-right'>Zones #</TableHead>
                    <TableHead>Slug</TableHead>
                    <TableHead>Time Zone</TableHead>
                    <TableHead className='text-right'>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedTenants.map((tenant, idx) => {
                    const rowNumber = tenantsPageStart + idx + 1;
                    const isActive = tenant.status === 'active';
                    // Paying customers (real dealer accounts, not demo/prospect)
                    // get a subtle emerald background tint so they pop. The
                    // predicate is computed once in payingCustomerIds and reused
                    // for both this highlight AND the Active Tenants KPI so the
                    // two are always in sync. Inactive rows fade.
                    const isPayingCustomer = payingCustomerIds.has(tenant.id);
                    return (
                    <TableRow
                      key={tenant.id}
                      className={cn(!isActive && 'opacity-60')}
                    >
                      <TableCell className='text-center text-xs text-muted-foreground tabular-nums'>
                        {rowNumber}
                      </TableCell>
                      <TableCell className='font-medium'>
                        <div className='flex items-center gap-1.5'>
                          {isPayingCustomer && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <BadgeCheck className='h-4 w-4 text-emerald-600 dark:text-emerald-400 flex-shrink-0' />
                              </TooltipTrigger>
                              <TooltipContent>Paying customer</TooltipContent>
                            </Tooltip>
                          )}
                          <span>{tenant.tenant_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {tenant.tenant_type ? (
                          <Badge variant='outline'>{tenant.tenant_type}</Badge>
                        ) : (
                          <span className='text-muted-foreground'>—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {tenant.oems && tenant.oems.length > 0 ? (
                          <div className='flex flex-wrap gap-1'>
                            {tenant.oems.slice(0, 2).map(oem => {
                              const theme = getOemTheme(oem);
                              return (
                                <Badge
                                  key={oem}
                                  variant='outline'
                                  className={cn('font-medium', theme.className)}
                                >
                                  {oem}
                                </Badge>
                              );
                            })}
                            {tenant.oems.length > 2 && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant='outline' className='cursor-help'>
                                    +{tenant.oems.length - 2}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <div className='flex flex-col gap-1'>
                                    {tenant.oems.slice(2).map(o => (
                                      <span key={o}>{o}</span>
                                    ))}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        ) : (
                          <span className='text-muted-foreground'>—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {isActive ? (
                          <Badge variant='default' className='bg-green-500'>
                            <CheckCircle className='h-3 w-3 mr-1' />
                            Active
                          </Badge>
                        ) : (
                          <Badge variant='secondary'>
                            <XCircle className='h-3 w-3 mr-1' />
                            Inactive
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className='text-right tabular-nums'>
                        {tenant.tenant_type === 'internal' ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant='outline'
                                className='text-xs bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-900 cursor-help'
                              >
                                <CheckCircle className='h-3 w-3 mr-1' />
                                All
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              Internal tenants have access to all zones automatically
                            </TooltipContent>
                          </Tooltip>
                        ) : tenant.zones && tenant.zones.length > 0 ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className='cursor-help font-medium'>
                                {tenant.zones.length}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className='flex flex-col gap-1 max-w-xs'>
                                {tenant.zones.slice(0, 8).map(zone => (
                                  <span key={zone.id}>{zone.zone_name}</span>
                                ))}
                                {tenant.zones.length > 8 && (
                                  <span className='text-muted-foreground'>
                                    +{tenant.zones.length - 8} more
                                  </span>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <span className='text-muted-foreground'>0</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <code className='text-xs bg-muted px-2 py-1 rounded'>
                          {tenant.tenant_slug || 'N/A'}
                        </code>
                      </TableCell>
                      <TableCell>
                        {tenant.time_zone || <span className='text-muted-foreground'>—</span>}
                      </TableCell>
                      <TableCell className='text-right'>
                        <div className='flex items-center justify-end gap-2'>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant='ghost'
                                size='sm'
                                onClick={() => openEditDialog(tenant)}
                                aria-label={`Edit ${tenant.tenant_name}`}
                              >
                                <Edit className='h-4 w-4' />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Edit</TooltipContent>
                          </Tooltip>

                          {/* Active tenants: delete (soft-delete to inactive).
                              Inactive tenants: restore (set status back to active).
                              We swap the icon rather than show both because only
                              one of these actions makes sense for a given row. */}
                          {isActive ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant='ghost'
                                  size='sm'
                                  onClick={() => handleDelete(tenant)}
                                  className='text-destructive hover:text-destructive'
                                  aria-label={`Delete ${tenant.tenant_name}`}
                                >
                                  <Trash2 className='h-4 w-4' />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Delete</TooltipContent>
                            </Tooltip>
                          ) : (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant='ghost'
                                  size='sm'
                                  onClick={() => void handleRestore(tenant)}
                                  disabled={restoringTenantId === tenant.id}
                                  className='text-emerald-700 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-400'
                                  aria-label={`Restore ${tenant.tenant_name}`}
                                >
                                  {restoringTenantId === tenant.id ? (
                                    <Loader2 className='h-4 w-4 animate-spin' />
                                  ) : (
                                    <RotateCcw className='h-4 w-4' />
                                  )}
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Restore</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            </TooltipProvider>
          )}
          {filteredTenantCount > 0 && (
            <div className={TENANTS_PAGINATION_FOOTER_CLASS}>
              <p className='text-sm text-muted-foreground'>
                Showing <span className='font-medium text-foreground'>{tenantsPageStart + 1}</span>–
                <span className='font-medium text-foreground'>{tenantsRangeEnd}</span>
                <span> of </span>
                <span className='font-medium text-foreground'>{filteredTenantCount}</span>
              </p>
              <div className='flex flex-wrap items-center gap-2'>
                <Select
                  value={String(tenantsPageSize)}
                  onValueChange={v => setTenantsPageSize(Number(v))}
                >
                  <SelectTrigger className='w-[120px]'>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TENANTS_PAGE_SIZE_OPTIONS.map(n => (
                      <SelectItem key={n} value={String(n)}>
                        {n} per page
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className='flex items-center gap-1'>
                  <Button
                    type='button'
                    variant='outline'
                    size='icon'
                    className='h-8 w-8'
                    disabled={tenantsSafePageIndex <= 0}
                    onClick={() => setTenantsPageIndex(p => Math.max(0, p - 1))}
                    aria-label='Previous page'
                  >
                    <ChevronLeft className='h-4 w-4' />
                  </Button>
                  <span className='min-w-[7rem] text-center text-sm text-muted-foreground'>
                    Page {tenantsSafePageIndex + 1} of {tenantsTotalPages}
                  </span>
                  <Button
                    type='button'
                    variant='outline'
                    size='icon'
                    className='h-8 w-8'
                    disabled={tenantsSafePageIndex >= tenantsTotalPages - 1}
                    onClick={() => setTenantsPageIndex(p => Math.min(tenantsTotalPages - 1, p + 1))}
                    aria-label='Next page'
                  >
                    <ChevronRight className='h-4 w-4' />
                  </Button>
                </div>
              </div>
            </div>
          )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        {/* max-w-5xl, not 3xl: the Zones tab is a dual-pane transfer list and at
            768px each pane got ~336px — too cramped for name + size + recall
            count. Widened once for the whole dialog rather than per tab, since a
            dialog that resizes as you switch tabs reads as a glitch. */}
        <DialogContent className='max-w-5xl'>
          <DialogHeader>
            <DialogTitle>{editingTenant ? 'Edit Tenant' : 'Create New Tenant'}</DialogTitle>
            <DialogDescription>
              {editingTenant
                ? 'Update tenant configuration and zone associations'
                : 'Configure a new tenant with type and zone associations'}
            </DialogDescription>
          </DialogHeader>

          {/* Tabbed form — splits the previously-scrolling form into 4
              focused tabs so each tab fits without scrolling. Tab order
              roughly matches frequency of edit (Basics most often). */}
          <Tabs defaultValue='basics' className='w-full'>
            <TabsList className='grid grid-cols-4 w-full'>
              <TabsTrigger value='basics'>Basics</TabsTrigger>
              <TabsTrigger value='location'>Location</TabsTrigger>
              <TabsTrigger value='contact'>Contact</TabsTrigger>
              <TabsTrigger value='zones'>Zones</TabsTrigger>
            </TabsList>

            {/* TAB 1: Basics — name, slug, type, status, description */}
            <TabsContent value='basics' className='mt-4 min-h-[420px]'>
              <div className='space-y-4'>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='tenant_name'>
                      Tenant Name <span className='text-destructive'>*</span>
                    </Label>
                    <Input
                      id='tenant_name'
                      value={formData.tenant_name}
                      onChange={e =>
                        setFormData(prev => ({ ...prev, tenant_name: e.target.value }))
                      }
                      placeholder='e.g., Acme Dealership'
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='tenant_slug'>Slug</Label>
                    <Input
                      id='tenant_slug'
                      value={formData.tenant_slug}
                      onChange={e =>
                        setFormData(prev => ({ ...prev, tenant_slug: e.target.value }))
                      }
                      placeholder='e.g., acme-dealership'
                    />
                  </div>
                </div>

                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='tenant_type'>Type</Label>
                    <Select
                      value={
                        formData.tenant_type && formData.tenant_type.trim() !== ''
                          ? formData.tenant_type
                          : undefined
                      }
                      onValueChange={value => {
                        setFormData(prev => ({
                          ...prev,
                          tenant_type: value,
                          zone_ids: value === 'internal' ? [] : prev.zone_ids
                        }));
                      }}
                    >
                      <SelectTrigger id='tenant_type'>
                        <SelectValue placeholder='Select type' />
                      </SelectTrigger>
                      <SelectContent className='z-[10000]'>
                        <SelectItem value='dealer'>Dealer</SelectItem>
                        <SelectItem value='internal'>Internal</SelectItem>
                        <SelectItem value='partner'>Partner</SelectItem>
                        <SelectItem value='test'>Test</SelectItem>
                        {tenantTypes
                          .filter(
                            type => !['dealer', 'internal', 'partner', 'test'].includes(type)
                          )
                          .map(type => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='status'>Status</Label>
                    <Select
                      value={formData.status || undefined}
                      onValueChange={value => {
                        setFormData(prev => ({ ...prev, status: value }));
                      }}
                    >
                      <SelectTrigger id='status'>
                        <SelectValue placeholder='Select status' />
                      </SelectTrigger>
                      <SelectContent className='z-[10000]'>
                        <SelectItem value='active'>Active</SelectItem>
                        <SelectItem value='inactive'>Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* OEM picker — moved here from the Zones tab so the
                    "what kind of dealer" cluster (Type, Status, OEM) is in
                    one place. The Zones tab still depends on this selection. */}
                <div className='space-y-2'>
                  <Label>
                    OEM{formData.oems.length > 1 ? 's' : ''}
                    {formData.tenant_type === 'dealer' && (
                      <span className='text-destructive ml-1'>*</span>
                    )}
                  </Label>
                  <Popover onOpenChange={open => !open && setOemSearch('')}>
                    <PopoverTrigger asChild>
                      <Button
                        variant='outline'
                        className='w-full justify-between font-normal'
                        type='button'
                      >
                        {formData.oems.length === 0 ? (
                          <span className='text-muted-foreground'>Select OEM(s)...</span>
                        ) : formData.oems.length === 1 ? (
                          formData.oems[0]
                        ) : (
                          `${formData.oems.length} OEMs selected`
                        )}
                        <ChevronDown className='h-4 w-4 ml-2 opacity-50' />
                      </Button>
                    </PopoverTrigger>
                    {/* portal={false} keeps the popover inside the dialog, which is
                        what lets the list below scroll — see PopoverContentProps. */}
                    <PopoverContent
                      className='w-[300px] p-0 z-[10000]'
                      align='start'
                      portal={false}
                    >
                      <div className='border-b p-2'>
                        <Input
                          value={oemSearch}
                          onChange={e => setOemSearch(e.target.value)}
                          placeholder='Search OEMs...'
                          className='h-8'
                        />
                      </div>
                      {/* Height is capped by the popover's own available space so the
                          list never overhangs the viewport and hides its scrollbar. */}
                      <div
                        className='space-y-2 overflow-y-auto p-3'
                        style={{ maxHeight: OEM_LIST_MAX_HEIGHT }}
                      >
                        {visibleOems.length === 0 && (
                          <p className='px-1 py-2 text-sm text-muted-foreground'>
                            No OEM matches “{oemSearch.trim()}”.
                          </p>
                        )}
                        {visibleOems.map(oem => (
                          <div key={oem} className='flex items-center space-x-2'>
                            <Checkbox
                              id={`oem-${oem}`}
                              checked={formData.oems.includes(oem)}
                              onCheckedChange={checked => {
                                setFormData(prev => {
                                  const newOems = checked
                                    ? [...prev.oems, oem]
                                    : prev.oems.filter(o => o !== oem);
                                  // Resolve against the unfiltered list:
                                  // filteredZones derives from prev.oems, which
                                  // is the very thing changing here.
                                  const validZoneIds = checked
                                    ? prev.zone_ids
                                    : zoneIdsWithoutOem(prev.zone_ids, allActiveZones, oem);
                                  return {
                                    ...prev,
                                    oems: newOems,
                                    zone_ids: validZoneIds
                                  };
                                });
                              }}
                            />
                            <Label
                              htmlFor={`oem-${oem}`}
                              className='font-normal cursor-pointer flex-1 flex items-center gap-2'
                            >
                              <span
                                className={cn(
                                  'inline-block h-2 w-2 rounded-full',
                                  getOemTheme(oem).dotClassName
                                )}
                              />
                              {oem}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                  {/* Selected OEM color chips for instant visual confirmation */}
                  {formData.oems.length > 0 && (
                    <div className='flex flex-wrap gap-1.5 pt-2'>
                      {formData.oems.map(oem => {
                        const theme = getOemTheme(oem);
                        return (
                          <Badge
                            key={oem}
                            variant='outline'
                            className={cn('font-medium', theme.className)}
                          >
                            {oem}
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                  {formData.tenant_type === 'dealer' && formData.oems.length === 0 && (
                    <p className='text-xs text-destructive'>At least one required for dealers</p>
                  )}
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='description'>Description</Label>
                  <Textarea
                    id='description'
                    value={formData.description}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, description: e.target.value }))
                    }
                    placeholder='Tenant description...'
                    rows={4}
                  />
                </div>
              </div>
            </TabsContent>

            {/* TAB: Zones — zone associations (OEM picker now lives in Basics) */}
            <TabsContent value='zones' className='mt-4 min-h-[420px]'>
              <div className='space-y-4'>
                <div className='space-y-2'>
                  <Label>Zones</Label>
                  {formData.tenant_type === 'internal' ? (
                    <div className='border rounded-md p-4 bg-muted/50'>
                      <div className='flex items-start gap-3'>
                        <CheckCircle className='h-5 w-5 text-green-600 mt-0.5 flex-shrink-0' />
                        <div>
                          <p className='text-sm font-medium text-foreground'>
                            All zones available by default
                          </p>
                          <p className='text-xs text-muted-foreground mt-1'>
                            Internal tenants automatically have access to all zones. Zone
                            selection is not required.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : formData.oems.length === 0 ? (
                    <div className='border rounded-md p-4 bg-muted/50'>
                      <div className='flex items-start gap-3'>
                        <Info className='h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0' />
                        <div>
                          <p className='text-sm font-medium text-foreground'>Select OEM(s) first</p>
                          <p className='text-xs text-muted-foreground mt-1'>
                            Zones are filtered by OEM. Please select one or more OEMs above to see
                            available zones.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <ZoneAssignmentPanel
                      zones={filteredZones}
                      selectedIds={formData.zone_ids}
                      onChange={ids => setFormData(prev => ({ ...prev, zone_ids: ids }))}
                      zoneOwners={zoneOwners}
                      editingTenantId={editingTenant?.id}
                      isLoading={loadingZones}
                      showOemGroups={formData.oems.length > 1}
                      assignmentDates={zoneAssignmentDates}
                    />
                  )}
                </div>
              </div>
            </TabsContent>

            {/* TAB 3: Location — time zone, country, address, lat/lon */}
            <TabsContent value='location' className='mt-4 min-h-[420px]'>
              <div className='space-y-4'>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='time_zone'>Time Zone</Label>
                    <Select
                      value={
                        formData.time_zone && formData.time_zone.trim() !== ''
                          ? formData.time_zone
                          : undefined
                      }
                      onValueChange={value => {
                        setFormData(prev => ({ ...prev, time_zone: value }));
                      }}
                    >
                      <SelectTrigger id='time_zone'>
                        <SelectValue placeholder='Select time zone' />
                      </SelectTrigger>
                      <SelectContent className='z-[10000]'>
                        <SelectItem value='America/New_York'>Eastern Time (ET)</SelectItem>
                        <SelectItem value='America/Chicago'>Central Time (CT)</SelectItem>
                        <SelectItem value='America/Denver'>Mountain Time (MT)</SelectItem>
                        <SelectItem value='America/Phoenix'>
                          Mountain Time - Arizona (MST)
                        </SelectItem>
                        <SelectItem value='America/Los_Angeles'>Pacific Time (PT)</SelectItem>
                        <SelectItem value='America/Anchorage'>Alaska Time (AKT)</SelectItem>
                        <SelectItem value='Pacific/Honolulu'>Hawaii Time (HST)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='country'>Country</Label>
                    <Input
                      id='country'
                      value={formData.country}
                      onChange={e => setFormData(prev => ({ ...prev, country: e.target.value }))}
                      placeholder='e.g., US'
                    />
                  </div>
                </div>

                <div className='space-y-2'>
                  <Label htmlFor='address_line1'>Address Line 1</Label>
                  <Input
                    id='address_line1'
                    value={formData.address_line1}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, address_line1: e.target.value }))
                    }
                    placeholder='Street address'
                  />
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='address_line2'>Address Line 2</Label>
                  <Input
                    id='address_line2'
                    value={formData.address_line2}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, address_line2: e.target.value }))
                    }
                    placeholder='Suite, unit, building, floor, etc.'
                  />
                </div>
                <div className='grid grid-cols-3 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='city'>City</Label>
                    <Input
                      id='city'
                      value={formData.city}
                      onChange={e => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      placeholder='City'
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='state'>State/Province</Label>
                    <Input
                      id='state'
                      value={formData.state}
                      onChange={e => setFormData(prev => ({ ...prev, state: e.target.value }))}
                      placeholder='State'
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='postal_code'>Postal Code</Label>
                    <Input
                      id='postal_code'
                      value={formData.postal_code}
                      onChange={e =>
                        setFormData(prev => ({ ...prev, postal_code: e.target.value }))
                      }
                      placeholder='ZIP/Postal'
                    />
                  </div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='latitude'>
                      Latitude
                      {formData.tenant_type === 'dealer' && (
                        <span className='text-destructive ml-1'>*</span>
                      )}
                    </Label>
                    <Input
                      id='latitude'
                      type='number'
                      step='any'
                      value={formData.latitude}
                      onChange={e =>
                        setFormData(prev => ({ ...prev, latitude: e.target.value }))
                      }
                      placeholder='e.g., 40.7128'
                      required={formData.tenant_type === 'dealer'}
                    />
                    <p className='text-xs text-muted-foreground'>
                      Decimal degrees (-90 to 90)
                      {formData.tenant_type === 'dealer' && (
                        <span className='text-destructive ml-1'>· Required for dealers</span>
                      )}
                    </p>
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='longitude'>
                      Longitude
                      {formData.tenant_type === 'dealer' && (
                        <span className='text-destructive ml-1'>*</span>
                      )}
                    </Label>
                    <Input
                      id='longitude'
                      type='number'
                      step='any'
                      value={formData.longitude}
                      onChange={e =>
                        setFormData(prev => ({ ...prev, longitude: e.target.value }))
                      }
                      placeholder='e.g., -74.0060'
                      required={formData.tenant_type === 'dealer'}
                    />
                    <p className='text-xs text-muted-foreground'>
                      Decimal degrees (-180 to 180)
                      {formData.tenant_type === 'dealer' && (
                        <span className='text-destructive ml-1'>· Required for dealers</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* TAB 4: Contact — email, phone, website */}
            <TabsContent value='contact' className='mt-4 min-h-[420px]'>
              <div className='space-y-4'>
                <div className='grid grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='primary_email'>Primary Email</Label>
                    <Input
                      id='primary_email'
                      type='email'
                      value={formData.primary_email}
                      onChange={e =>
                        setFormData(prev => ({ ...prev, primary_email: e.target.value }))
                      }
                      placeholder='contact@example.com'
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='primary_phone'>Primary Phone</Label>
                    <Input
                      id='primary_phone'
                      value={formData.primary_phone}
                      onChange={e =>
                        setFormData(prev => ({ ...prev, primary_phone: e.target.value }))
                      }
                      placeholder='+1 (555) 123-4567'
                    />
                  </div>
                </div>
                <div className='space-y-2'>
                  <Label htmlFor='website'>Website</Label>
                  <Input
                    id='website'
                    type='url'
                    value={formData.website}
                    onChange={e => setFormData(prev => ({ ...prev, website: e.target.value }))}
                    placeholder='https://www.example.com'
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant='outline' onClick={() => setDialogOpen(false)} disabled={formLoading}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={formLoading}>
              {formLoading && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
              {editingTenant ? 'Update' : 'Create'} Tenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Zone-change confirmation — interrupts edits that touch zone
          associations so an accidental click can't reshape what data the
          customer can see. Removed zones get red treatment, added zones
          get emerald treatment. Shown only when zones differ. */}
      <Dialog open={zoneDiffConfirmOpen} onOpenChange={setZoneDiffConfirmOpen}>
        <DialogContent className='max-w-lg'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2'>
              <AlertTriangle className='h-5 w-5 text-amber-500' />
              Confirm zone changes
            </DialogTitle>
            <DialogDescription>
              You're about to change the zones mapped to{' '}
              <span className='font-semibold text-foreground'>
                {editingTenant?.tenant_name}
              </span>
              . This affects what data they can access in the app.
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-4 py-2 max-h-[50vh] overflow-y-auto'>
            {zoneDiff.removed.length > 0 && (
              <div className='space-y-2'>
                <div className='flex items-center gap-2 text-sm font-semibold text-red-700 dark:text-red-400'>
                  <XCircle className='h-4 w-4' />
                  Removing access to {zoneDiff.removed.length} zone
                  {zoneDiff.removed.length === 1 ? '' : 's'}
                </div>
                <div className='rounded-md border border-red-200 bg-red-50/50 dark:bg-red-950/20 dark:border-red-900 p-3 space-y-1.5'>
                  {zoneDiff.removed.map(z => {
                    const recalls = removedRecallData?.counts?.[z.id];
                    return (
                      <div key={z.id} className='flex items-center gap-2 text-sm'>
                        <MapPin className='h-3.5 w-3.5 text-muted-foreground flex-shrink-0' />
                        <span className='font-medium'>{z.zone_name}</span>
                        {/* What the tenant actually loses, not just the zone name. */}
                        {recalls !== undefined && (
                          <span className='ml-auto text-xs tabular-nums text-muted-foreground'>
                            {recalls.toLocaleString()} recall{recalls === 1 ? '' : 's'}
                          </span>
                        )}
                        {z.oem && (
                          <Badge
                            variant='outline'
                            className={cn(
                              'text-xs',
                              recalls === undefined && 'ml-auto',
                              getOemTheme(z.oem).className
                            )}
                          >
                            {z.oem}
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {zoneDiff.added.length > 0 && (
              <div className='space-y-2'>
                <div className='flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-400'>
                  <CheckCircle className='h-4 w-4' />
                  Granting access to {zoneDiff.added.length} zone
                  {zoneDiff.added.length === 1 ? '' : 's'}
                </div>
                <div className='rounded-md border border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/20 dark:border-emerald-900 p-3 space-y-1.5'>
                  {zoneDiff.added.map(z => (
                    <div key={z.id} className='flex items-center gap-2 text-sm'>
                      <MapPin className='h-3.5 w-3.5 text-muted-foreground flex-shrink-0' />
                      <span className='font-medium'>{z.zone_name}</span>
                      {z.oem && (
                        <Badge
                          variant='outline'
                          className={cn('text-xs ml-auto', getOemTheme(z.oem).className)}
                        >
                          {z.oem}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setZoneDiffConfirmOpen(false)}
              disabled={formLoading}
            >
              Keep existing zones
            </Button>
            <Button onClick={() => void executeSubmit()} disabled={formLoading}>
              {formLoading && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
              Confirm zone changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation — replaces the previous window.confirm() prompt.
          Shows tenant name + zone count and explains the soft-delete
          semantics so users know they can recover. */}
      <Dialog
        open={deleteConfirmOpen}
        onOpenChange={open => {
          if (!open) {
            setDeleteConfirmOpen(false);
            setTenantToDelete(null);
          }
        }}
      >
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-2 text-destructive'>
              <AlertTriangle className='h-5 w-5' />
              Delete tenant?
            </DialogTitle>
            <DialogDescription>
              You're about to delete{' '}
              <span className='font-semibold text-foreground'>
                {tenantToDelete?.tenant_name}
              </span>
              . The tenant will be hidden from the active list but data is preserved and can be
              restored if needed.
            </DialogDescription>
          </DialogHeader>

          {tenantToDelete && (
            <div className='space-y-2 py-2'>
              <div className='rounded-md border bg-muted/30 p-3 space-y-1 text-sm'>
                <div className='flex justify-between'>
                  <span className='text-muted-foreground'>Type</span>
                  <span className='font-medium'>{tenantToDelete.tenant_type || '—'}</span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-muted-foreground'>Status</span>
                  <span className='font-medium capitalize'>
                    {tenantToDelete.status || '—'}
                  </span>
                </div>
                <div className='flex justify-between'>
                  <span className='text-muted-foreground'>Zones mapped</span>
                  <span className='font-medium tabular-nums'>
                    {tenantToDelete.zones?.length ?? 0}
                  </span>
                </div>
              </div>
              {payingCustomerIds.has(tenantToDelete.id) && (
                <div className='flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50/60 dark:bg-amber-950/20 dark:border-amber-900 p-3 text-sm'>
                  <BadgeCheck className='h-4 w-4 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0' />
                  <div>
                    <span className='font-semibold'>This is a paying customer.</span> Double-check
                    before deleting — they will lose access immediately.
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setDeleteConfirmOpen(false);
                setTenantToDelete(null);
              }}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={() => void executeDelete()}
              disabled={deleteLoading}
            >
              {deleteLoading && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
              Delete tenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
